/**
 * Record-access dimension options (DSCO-5543 — Node Based Access Control).
 *
 * One hook serves every dimension. The dimension's `name` — `'team'`, `'node'`,
 * or a future identifier — *is* the search entity name, so the already-generic
 * `entity.search` registry entry resolves the options with no hardcoded entity
 * (HLD v0.3.1 Design E). Read the name off `useCurrentUser().activeDimension`
 * rather than hardcoding it at a call site.
 *
 * The rows `/search` returns are curated server-side to the actor's reach, so
 * this is both the picker's option list and the actor's own allowed set — there
 * is no second call for either.
 */

import { useMemo } from 'react';
import { useApiQuery, type ApiError } from '@locus-taxy/encore-ui-network';
import {
  resolveEntityDisplayValue,
  useToast,
} from '@locus-taxy/encore-ui-toolkit';
import type { EntitySearchResponse } from './useEntitySearch';

/**
 * A single option row, normalised from `POST .../entities/{name}/search`.
 *
 * `label` is this shape's own field, not the wire's: a dimension source entity
 * carries a **`name`**, which is what `/search` returns (`team` rows come back
 * as `{ id, name, status }`). Projecting or reading `entityData.label` yields
 * `undefined` for every row and renders the picker and the sidebar as blank
 * lines — see {@link toOption}, which is the one place the rename happens.
 *
 * `color` is optional by design — Design C specifies a two-tier colour rule:
 * the per-value colour when `/search` supplies one, neutral grey otherwise.
 *
 * It arrives as `entityData.colorHex.hexCode`, a nested object, not a flat
 * `color` string. This hook projected and read `entityData.color`, which no
 * dimension entity has, so every row came back colourless and every chip
 * rendered the neutral tier — which read as "no entity carries a colour"
 * rather than as the mis-named field it actually was.
 */
export interface AccessDimensionOption {
  id: string;
  label: string;
  color?: string;
}

/** The wire shape of one dimension-source-entity row. */
interface AccessDimensionRow {
  id: string;
  name?: string;
  code?: string;
  /** Nested, and often present-but-empty (`colorHex: {}`) — see {@link toOption}. */
  colorHex?: { hexCode?: string };
}

/** Wire row → option. The label follows the shared rule (DP-5812): code, then name, then id. */
const toOption = (row: AccessDimensionRow): AccessDimensionOption => {
  // `colorHex` is routinely present but empty (`{}`) for a value with no colour
  // set, so presence is not enough — the hex itself has to be there.
  const hex = row.colorHex?.hexCode?.trim();
  return {
    id: row.id,
    label: resolveEntityDisplayValue(
      { code: row.code, name: row.name },
      row.id
    ),
    ...(hex ? { color: hex } : {}),
  };
};

/** The dimension reach is bounded well below this; it is a safety ceiling. */
const OPTIONS_PAGE_SIZE = 1000;

/**
 * Fetch every option the actor can reach on one record-access dimension.
 *
 * The query is keyed on `dimensionName`, so a deployment that switches
 * dimensions — or a test that renders both — never reads one dimension's rows
 * out of another's cache entry. It stays disabled until both the client and the
 * dimension name are known, which is the `no-dimensions` case (enforcement
 * disabled): no request goes out and `data` is an empty array.
 */
export function useAccessDimensionOptions(
  clientId: string,
  dimensionName: string
) {
  const { showApiError } = useToast();

  const searchRequest = useMemo(
    () => ({
      complexQuery: {}, // Server curates to the actor's reach; no client filter.
      projection: {
        'entityData.id': true,
        // `name`, not `label` — see `AccessDimensionOption`. Projecting a field
        // the entity does not have returns rows with no display text at all.
        'entityData.name': true,
        'entityData.code': true,
        // `colorHex`, not `color` — the wire field is a nested object
        // (`{ hexCode: '#b91c1c' }`). Projecting `color` returned nothing, which
        // is why every chip rendered the neutral tier.
        'entityData.colorHex': true,
      },
      pagination: {
        size: OPTIONS_PAGE_SIZE,
      },
    }),
    []
  );

  const {
    data: optionsResponse,
    isLoading,
    error,
    refetch,
  } = useApiQuery<EntitySearchResponse<AccessDimensionRow>>({
    key: ['accessDimensionOptions', clientId, dimensionName],
    endpointKey: 'entity.search',
    config: {
      method: 'POST',
      body: { request: searchRequest },
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      enabled: Boolean(clientId && dimensionName),
      onError: (error: ApiError) => {
        showApiError(error);
      },
    },
    params: { clientId, entityName: dimensionName },
  });

  const options = useMemo<AccessDimensionOption[]>(() => {
    if (!optionsResponse?.data?.content) {
      return [];
    }
    const { content, totalElements } = optionsResponse.data;
    if (totalElements > content.length) {
      // Diagnostic only; the selector would otherwise silently hide options the
      // actor can legitimately reach, with no signal that OPTIONS_PAGE_SIZE was
      // the cause. The directive sits on its own line — with the rationale
      // trailing it, "next line" was the comment rather than the call.
      // eslint-disable-next-line no-console
      console.warn(
        `[useAccessDimensionOptions] ${dimensionName}: ${totalElements} options exist but only ${content.length} were fetched (page size ${OPTIONS_PAGE_SIZE}).`
      );
    }
    return content.map(item => toOption(item.entityData));
  }, [optionsResponse, dimensionName]);

  return {
    data: options,
    refetch,
    isLoading,
    error,
    isError: !!error,
  };
}
